import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight } from "lucide-react";

const skills = [
  "OpenAI & GPT-4o",
  "n8n & Make.com",
  "WhatsApp Business API",
  "Python & FastAPI",
  "Retell AI Voice",
  "CRM Integrations",
  "Zapier & Pipedream",
  "Custom AI Agents",
];

const highlights = [
  "5+ years building AI & software systems",
  "Direct, founder-led communication only",
  "Delivered 150+ automation systems",
  "24/7 async support on every project",
];

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-36 relative">
      <div className="section-divider" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 xl:gap-24 items-center">

          {/* Text side */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="overline-badge mb-5">About</p>
            <h2 className="text-[2.2rem] sm:text-[2.8rem] lg:text-[3.4rem] font-black tracking-[-0.04em] text-white leading-[1.0] mb-7">
              Automation built by{" "}
              <span className="gradient-text-static">someone who cares.</span>
            </h2>
            <p className="text-[15px] text-white/48 leading-[1.75] mb-5 tracking-[-0.01em]">
              I'm a passionate AI automation builder focused on creating practical systems that help businesses save time, increase efficiency, and scale faster using modern AI tools.
            </p>
            <p className="text-[15px] text-white/35 leading-[1.75] mb-9 tracking-[-0.01em]">
              I don't believe in over-complicated solutions. Every system I build is designed to solve a real problem, integrate cleanly into your existing workflow, and deliver measurable results from day one.
            </p>

            {/* Highlights */}
            <ul className="space-y-3 mb-10">
              {highlights.map((h) => (
                <li key={h} className="flex items-center gap-3">
                  <CheckCircle2 className="w-4 h-4 text-blue-400/70 flex-shrink-0" strokeWidth={1.5} />
                  <span className="text-[13.5px] text-white/50">{h}</span>
                </li>
              ))}
            </ul>

            <a
              href="#cta"
              data-testid="about-cta"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/[0.12] text-[13.5px] text-white/70 hover:text-white font-semibold transition-all duration-200"
            >
              Let's work together
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
          </motion.div>

          {/* Portrait + skills */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col items-center lg:items-start gap-7"
          >
            {/* Portrait frame */}
            <div className="relative">
              {/* Outer glow */}
              <div className="absolute -inset-6 rounded-3xl pointer-events-none" style={{ background: "radial-gradient(ellipse at center, rgba(59,130,246,0.1) 0%, rgba(139,92,246,0.06) 40%, transparent 65%)" }} />
              <div className="relative w-full max-w-sm h-[360px] rounded-2xl overflow-hidden border border-white/[0.08] card-inner-top">
                <img
                  src="https://customer-assets.emergentagent.com/job_ai-workflow-pro-27/artifacts/7tf35l5j_self_photo.jpeg"
                  alt="AIFlow Labs Founder"
                  className="w-full h-full object-cover object-center"
                  style={{ filter: "contrast(1.02) brightness(0.92)" }}
                />
                {/* Bottom overlay */}
                <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-[#07070A]/90 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[13px] font-semibold text-white/90">Founder, AIFlow Labs</p>
                      <p className="text-[11px] text-white/40">AI Automation Specialist</p>
                    </div>
                    <div className="flex items-center gap-1.5 bg-emerald-500/15 border border-emerald-500/20 px-2.5 py-1 rounded-full">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      <span className="text-[10px] text-emerald-400 font-medium">Available</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tech stack */}
            <div className="flex flex-wrap gap-2 max-w-sm">
              {skills.map((s) => (
                <span key={s} className="px-3 py-1.5 rounded-full text-[11.5px] font-medium border border-white/[0.06] bg-white/[0.02] text-white/40 hover:text-white/70 hover:border-white/[0.1] transition-all duration-200 cursor-default">
                  {s}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
